import { Stack, styled } from '@mui/material'
import {
    assembleAssetTitle,
    getMessagePreviewForThread,
    type ThreadCopy,
    getThreadType,
    isoDateToTimeIfTodayOrDate,
    LoadingSkeleton,
    TextWithEllipsis,
    ThreadAvatar,
    tokens,
    Typography as DeprecatedTypography,
    UserBubbleList,
    createAccountPresentation,
} from 'ComponentLibrary'

import { ThreadWithMetadata } from 'ComponentLibrary/src/common/threads'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { Address } from 'ComponentLibrary/src/common/address'
import { IconButtonWithOptions } from 'ComponentLibrary/src/piazza/buttons'
import { StyledLoadingDotsWrapper } from 'ComponentLibrary/src/atoms/LoadingIndicator/StyledLoadingDotsWrapper'
import { FilesIcon, PushPinIcon, PushPinSlash, TrashIcon } from '@phosphor-icons/react'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

export type Thread<TaskType> = ThreadWithMetadata<TaskType> & {
    order?: { uuid: UUID; address: Address; customer_asset_reference?: string }
}

export type UseDeleteThreadType = ({ customerId }: { customerId?: string }) => {
    deleteThread: (threadId: string) => void
    isDeleteThreadPending: boolean
}

type ThreadSelectionItemProps<TaskType> = {
    thread: Thread<TaskType>
    createMode: boolean
    activeThreadId: UUID | undefined
    onClickThread: (index: string) => void
    isLoadingOrderData?: boolean
    pinThread: (threadId: string) => void
    unpinThread: (threadId: string) => void
    useDeleteThread: UseDeleteThreadType
    customerId?: string
    copy?: Partial<ThreadSelectionItemCopy>
    userLinkCopy?: Partial<UserLinkCopy>
    locale?: string
}

export type ThreadSelectionItemCopy = {
    noTitle: string
    notifiedUsersTooltip: string
    options: string
    unpin: string
    pin: string
    delete: string
    thread: ThreadCopy
}

export const DEFAULT_THREAD_SELECTION_ITEM_COPY: ThreadSelectionItemCopy = {
    noTitle: 'Kein Titel',
    notifiedUsersTooltip: 'Über neue Nachrichten wird aktuell niemand benachrichtigt. Hier klicken, um Benachrichtigte hinzuzufügen.',
    options: 'Optionen',
    unpin: 'Unterhaltung abpinnen',
    pin: 'Unterhaltung pinnen',
    delete: 'Unterhaltung löschen',
    thread: {
        noMessages: 'Keine Nachrichten',
        deletedMessage: 'Nachricht wurde gelöscht',
        createdBy: (userName) => `Unterhaltung wurde von ${userName} angelegt`,
        createdAutomatically: 'Die Unterhaltung wurde automatisch angelegt',
    },
}

export function ThreadSelectionItem<TaskType>({
    thread,
    createMode,
    activeThreadId,
    onClickThread,
    isLoadingOrderData,
    pinThread,
    unpinThread,
    useDeleteThread,
    customerId,
    copy,
    userLinkCopy,
    locale = 'de-DE',
}: ThreadSelectionItemProps<TaskType>) {
    const resolvedCopy = { ...DEFAULT_THREAD_SELECTION_ITEM_COPY, ...copy }
    const threadCopy = { ...DEFAULT_THREAD_SELECTION_ITEM_COPY.thread, ...copy?.thread }
    const { deleteThread, isDeleteThreadPending } = useDeleteThread({ customerId })
    const isActive = !createMode && activeThreadId === thread.uuid
    const topic = thread.topic || resolvedCopy.noTitle
    const threadPreview = getMessagePreviewForThread(thread, threadCopy)
    const orderText = thread.order
        ? assembleAssetTitle({
              customer_asset_reference: thread.order.customer_asset_reference,
              street: thread.order.address.street,
              house_number: thread.order.address.house_number,
          })
        : undefined

    return (
        <ThreadOverviewItem
            onClick={() => onClickThread(thread.uuid)}
            $isActive={isActive}
            $isPinned={thread.isPinned}
            $isDeleting={isDeleteThreadPending}
        >
            {isDeleteThreadPending && <StyledLoadingDotsWrapper />}
            {thread.isPinned && (
                <PinIconContainer>
                    <PushPinIcon weight="bold" color={tokens.ColorIconBoldest} size={tokens.SizingIconS} />
                </PinIconContainer>
            )}
            <ThreadAvatar type={getThreadType(thread)} sx={{ alignSelf: 'center' }} />
            <Stack direction="row" width="100%" alignItems="center" justifyContent="space-between" gap={tokens.SizingSpacing150}>
                <Stack direction="row">
                    <Stack direction="column" maxWidth="250px">
                        <TextWithEllipsis tooltipText={topic} typographyProps={{ variant: 'bodyTextSmall' }}>
                            {topic}
                        </TextWithEllipsis>
                        {threadPreview}
                        {orderText && (
                            <Stack gap={tokens.SizingSpacing050} direction="row" marginTop={tokens.SizingSpacing100} alignItems="center">
                                <FilesIcon
                                    color={tokens.ColorIconDefault}
                                    size={tokens.SizingIconS}
                                    weight="bold"
                                    style={{ flexShrink: 0 }}
                                />
                                <TextWithEllipsis
                                    typographyProps={{
                                        variant: 'bodyTextSmall',
                                        color: tokens.ColorTextDefault,
                                    }}
                                    tooltipText={orderText}
                                >
                                    {orderText}
                                </TextWithEllipsis>
                            </Stack>
                        )}
                        {isLoadingOrderData && (
                            <LoadingSkeleton variant="text" height="18px" sx={{ transform: 'unset', marginTop: tokens.SizingSpacing100 }} />
                        )}
                    </Stack>
                </Stack>
                <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                    <Stack direction="column" alignItems="flex-end" gap={tokens.SizingSpacing075}>
                        <DeprecatedTypography variant="bodyTextSmall" color={tokens.ColorTextDefault}>
                            {isoDateToTimeIfTodayOrDate(thread.updated_on, locale)}
                        </DeprecatedTypography>
                        <UserBubbleList
                            tooltip={resolvedCopy.notifiedUsersTooltip}
                            presentations={thread.participant_users.map((user) => createAccountPresentation(user))}
                            userLinkCopy={userLinkCopy}
                        />
                    </Stack>
                    <IconButtonWithOptions
                        variant="tertiary"
                        size="small"
                        tooltip={resolvedCopy.options}
                        options={[
                            thread.isPinned && {
                                title: resolvedCopy.unpin,
                                icon: <PushPinSlash />,
                                callback: () => unpinThread(thread.uuid),
                            },
                            !thread.isPinned && {
                                title: resolvedCopy.pin,
                                icon: <PushPinIcon />,
                                callback: () => pinThread(thread.uuid),
                            },
                            !thread.associatedTask && {
                                title: resolvedCopy.delete,
                                icon: <TrashIcon />,
                                callback: () => deleteThread(thread.uuid),
                                danger: true,
                            },
                        ].filter((item) => !!item)}
                    />
                </Stack>
            </Stack>
        </ThreadOverviewItem>
    )
}

const ThreadOverviewItem = styled(Stack, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $isActive?: boolean; $isPinned?: boolean; $isDeleting: boolean }>(({ $isActive, $isPinned, $isDeleting }) => ({
    flexDirection: 'row',
    position: 'relative',
    padding: `${tokens.SizingSpacing150} ${tokens.SizingSpacing100} ${tokens.SizingSpacing150} ${tokens.SizingSpacing200}`,
    borderBottom: `0.5px solid rgba(0,0,0,0.05)`,
    alignItems: 'center',
    cursor: 'pointer',
    minHeight: '74px',
    width: '100%',
    pointerEvents: $isDeleting ? 'none' : undefined,
    gap: tokens.SizingSpacing150,
    backgroundColor: $isActive
        ? tokens.ColorSurfaceSelectedDefault
        : $isPinned
          ? tokens.ColorSurfaceAccentRoyalDefault
          : tokens.ColorBackgroundLight,
    '&:hover:not(:has(button:hover,a:hover))': {
        backgroundColor: tokens.ColorSurfaceHover,
    },
    '&:active:not(:has(button:active,a:active))': {
        backgroundColor: tokens.ColorSurfacePressed,
    },
}))

export const PinIconContainer = styled('div')(() => ({
    position: 'absolute',
    top: '8px',
    left: '8px',
    width: '13px',
    height: '13px',
    fontSize: '13px',
}))
